import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import TabHeader from '../../Components/TabHeader/TabHeader';
import { useClient } from '../../../../api/api-client';
import { useQueryCall } from '../../../../api/react-client';
import { PICKUP_POLICY_ENDPOINTS, PICKUP_POLICY_STATUS } from './constants';
import CategoryDropdown from './Elements/CategoryDropdown';
import ComplaintTypeDropdown from './Elements/ComplaintTypeDropdown';
import PickupPolicyResult from './Elements/PickupPolicyResult';
import Button from '../../../../components/ComponentsV1/Button';

const extractErrorMessage = (error, fallback) => {
  if (typeof error === 'string') return error;
  return error?.error_message || error?.message || error?.detail || error?.error || error?.data?.message || fallback;
};

const PickupPolicy = () => {
  const client = useClient();

  // Search input fields
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedComplaintType, setSelectedComplaintType] = useState(null);
  const [dropdownResetKey, setDropdownResetKey] = useState(0);

  // Result state
  const [policyStatus, setPolicyStatus] = useState(PICKUP_POLICY_STATUS.NONE);
  const [policyData, setPolicyData] = useState(null);

  // Loading states
  const [isFetching, setIsFetching] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const { data: metadata, isLoading: metadataLoading } = useQueryCall(
    {},
    PICKUP_POLICY_ENDPOINTS.METADATA,
    ['pickup_policy', 'metadata'],
    () => client(PICKUP_POLICY_ENDPOINTS.METADATA),
  );

  const categoryOptions =
    metadata?.categories?.map((cat) => ({
      label: cat.name,
      value: cat.name,
      id: cat.id,
      original_name: cat.original_name,
      level: cat.level,
    })) || [];

  const complaintTypeOptions =
    metadata?.complaint_types?.map((ct) => ({
      label: ct.complaint_type,
      value: ct.complaint_type,
      pickup_disabled: ct.pickup_disabled,
    })) || [];

  // Map operator value -> display label, e.g. 'gte' -> '>='
  const operatorsMap = Object.fromEntries(
    (metadata?.operators || []).map((op) => [op.value, op.label]),
  );

  const operators = metadata?.operators || [];
  const attributeValues = metadata?.attribute_values || [];

  // Resets result section only
  const resetStatusState = useCallback(() => {
    setPolicyStatus(PICKUP_POLICY_STATUS.NONE);
    setPolicyData(null);
  }, []);

  // Resets search inputs + result section
  const resetAllFields = useCallback(() => {
    setSelectedCategory(null);
    setSelectedComplaintType(null);
    setDropdownResetKey((k) => k + 1);
    resetStatusState();
  }, [resetStatusState]);

  const buildRequestBody = useCallback(
    (pickupPolicy) => ({
      policy_data: [
        {
          category_id: selectedCategory.id,
          complaint_type: selectedComplaintType.value,
          pickup_policy: pickupPolicy,
        },
      ],
    }),
    [selectedCategory, selectedComplaintType],
  );

  const handleSearchPolicy = async (e) => {
    e.preventDefault();

    if (!selectedCategory && !selectedComplaintType) {
      toast.error('Category and Complaint Type are required');
      return;
    }
    if (!selectedCategory) {
      toast.error('Category is required');
      return;
    }
    if (!selectedComplaintType) {
      toast.error('Complaint Type is required');
      return;
    }
    if (selectedComplaintType.pickup_disabled) {
      toast.error('Pickup is disabled for this complaint type. No policy actions are allowed.');
      return;
    }

    resetStatusState();
    setIsFetching(true);

    try {
      const params = new URLSearchParams({
        category_id: selectedCategory.id,
        complaint_type: selectedComplaintType.value,
      });
      const response = await client(
        `${PICKUP_POLICY_ENDPOINTS.FETCH}?${params}`,
      );

      if ('data' in response && response.data === null) {
        setPolicyStatus(PICKUP_POLICY_STATUS.NOT_FOUND);
      } else {
        setPolicyData(response);
        setPolicyStatus(PICKUP_POLICY_STATUS.EXISTS);
        toast.success('Policy found');
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to fetch policy'));
    } finally {
      setIsFetching(false);
    }
  };

  const submitPolicy = async (pickupPolicy, mode) => {
    const isUpdate = mode === 'update';
    setIsActionLoading(true);
    try {
      await client(PICKUP_POLICY_ENDPOINTS.SET, {
        data: buildRequestBody(pickupPolicy),
      });
      toast.success(isUpdate ? 'Policy updated successfully' : 'Policy created successfully');
      resetAllFields();
    } catch (error) {
      toast.error(extractErrorMessage(error, isUpdate ? 'Failed to update policy' : 'Failed to create policy'));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleUpdate = (pickupPolicy) => submitPolicy(pickupPolicy, 'update');
  const handleCreate = (pickupPolicy) => submitPolicy(pickupPolicy, 'create');

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10'>
      <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10 tw-bg-white tw-p-8 tw-rounded-2xl'>
        <TabHeader title='Pickup Policy' />

        <form onSubmit={handleSearchPolicy}>
          <div className='tw-flex tw-w-full tw-h-max tw-gap-5'>
            <CategoryDropdown
              key={`category-${dropdownResetKey}`}
              loading={metadataLoading}
              options={categoryOptions}
              value={selectedCategory}
              onChange={setSelectedCategory}
            />
            <ComplaintTypeDropdown
              key={`complaint-${dropdownResetKey}`}
              loading={metadataLoading}
              options={complaintTypeOptions}
              value={selectedComplaintType}
              onChange={setSelectedComplaintType}
            />
          </div>

          <div className='tw-mt-8'>
            <Button title='Search Policy' />
          </div>
        </form>
      </div>

      <PickupPolicyResult
        policyStatus={policyStatus}
        operatorsMap={operatorsMap}
        operators={operators}
        attributeValues={attributeValues}
        policyData={policyData}
        isFetching={isFetching}
        isActionLoading={isActionLoading}
        onUpdate={handleUpdate}
        onCreate={handleCreate}
      />
    </div>
  );
};

export default PickupPolicy;
